import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  COMPLIANCE_STUDIOS,
  MACHINERY_DIRECTIVE_STUDIO_ID,
} from '@artem-lex/common-server/complianceStudio'
import {
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from '@/shared/config/languages'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import { useListAssociatedPartiesForProduct } from '@/shared/hooks/api/associatedParties'
import { resolveUiLocale } from '@/shared/lib/standardEntityLocalizations'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'

type ProductComplianceStudioSelectDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  productId: string
  onStartStudio: (
    studioId: string,
    options?: { contentLanguage: SupportedLanguage }
  ) => void
  disableMachineryDirectiveOption: boolean
  isCreating: boolean
}

export function ProductComplianceStudioSelectDialog({
  open,
  onOpenChange,
  productId,
  onStartStudio,
  disableMachineryDirectiveOption,
  isCreating,
}: ProductComplianceStudioSelectDialogProps) {
  const { t, i18n } = useTranslation('productCatalog')
  const { data: session } = useGetUserSession()
  const isArtemAdmin = session?.isArtemAdmin ?? false
  const uiContentLanguage = useMemo(
    () => resolveUiLocale(i18n.language) as SupportedLanguage,
    [i18n.language]
  )
  const {
    data: productAssociatedParties = [],
    isLoading: isLoadingProductAssociatedParties,
  } = useListAssociatedPartiesForProduct(productId)
  const [selectedStudioId, setSelectedStudioId] = useState<string | null>(null)
  const [contentLanguage, setContentLanguage] =
    useState<SupportedLanguage>(uiContentLanguage)

  useEffect(() => {
    if (open) {
      setContentLanguage(uiContentLanguage)
    }
  }, [open, uiContentLanguage])

  const effectiveContentLanguage = isArtemAdmin
    ? contentLanguage
    : uiContentLanguage

  const visibleComplianceStudios = COMPLIANCE_STUDIOS

  const firstEnabledStudio = useMemo(
    () =>
      visibleComplianceStudios.find((studio) => {
        if (studio.id === MACHINERY_DIRECTIVE_STUDIO_ID) {
          return !disableMachineryDirectiveOption
        }
        return true
      }),
    [visibleComplianceStudios, disableMachineryDirectiveOption]
  )

  const effectiveSelectedStudioId =
    selectedStudioId ?? firstEnabledStudio?.id ?? null
  const hasProductManufacturer = productAssociatedParties.some(
    (assignment) => assignment.role === 'MANUFACTURER'
  )
  const isMachineryDirectiveSelected =
    effectiveSelectedStudioId === MACHINERY_DIRECTIVE_STUDIO_ID
  const requiresManufacturerBeforeCreate =
    isMachineryDirectiveSelected &&
    !disableMachineryDirectiveOption &&
    !isLoadingProductAssociatedParties &&
    !hasProductManufacturer
  const isWaitingForManufacturerCheck =
    isMachineryDirectiveSelected && isLoadingProductAssociatedParties
  const isSupportedStudio = effectiveSelectedStudioId !== null
  const canStart =
    isSupportedStudio &&
    !requiresManufacturerBeforeCreate &&
    !isWaitingForManufacturerCheck &&
    !isCreating &&
    (!isMachineryDirectiveSelected || !disableMachineryDirectiveOption)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>
            {t('detail.complianceStudios.dialog.title')}
          </DialogTitle>
          <DialogDescription>
            {t('detail.complianceStudios.dialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-3'>
          {visibleComplianceStudios.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              {t('detail.complianceStudios.dialog.empty')}
            </p>
          ) : (
            <div className='space-y-2'>
              <label
                htmlFor='product-compliance-studio-select'
                className='text-sm font-medium text-foreground'
              >
                {t('detail.complianceStudios.dialog.selectLabel')}
              </label>
              <Select
                value={effectiveSelectedStudioId ?? undefined}
                onValueChange={(value) => {
                  if (value) {
                    setSelectedStudioId(value)
                  }
                }}
                disabled={isCreating}
                items={visibleComplianceStudios.map((studio) => ({
                  value: studio.id,
                  label: studio.title,
                }))}
              >
                <SelectTrigger
                  id='product-compliance-studio-select'
                  className='w-full'
                >
                  <SelectValue
                    placeholder={t(
                      'detail.complianceStudios.dialog.selectPlaceholder'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {visibleComplianceStudios.map((studio) => {
                    const isMachineryDirectiveDisabled =
                      studio.id === MACHINERY_DIRECTIVE_STUDIO_ID &&
                      disableMachineryDirectiveOption
                    return (
                      <SelectItem
                        key={studio.id}
                        value={studio.id}
                        disabled={isMachineryDirectiveDisabled}
                      >
                        {studio.title}
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
              {requiresManufacturerBeforeCreate ? (
                <Alert variant='warning'>
                  <AlertTitle>
                    {t(
                      'detail.complianceStudios.dialog.manufacturerRequiredTitle'
                    )}
                  </AlertTitle>
                  <AlertDescription>
                    {t(
                      'detail.complianceStudios.dialog.manufacturerRequiredDescription'
                    )}
                  </AlertDescription>
                </Alert>
              ) : null}
              {isMachineryDirectiveSelected &&
              !disableMachineryDirectiveOption &&
              isArtemAdmin ? (
                <div className='space-y-2'>
                  <Label className='text-sm font-medium text-foreground'>
                    {t(
                      'detail.complianceStudios.dialog.contentLanguageLabel'
                    )}
                  </Label>
                  <p className='text-sm text-muted-foreground'>
                    {t(
                      'detail.complianceStudios.dialog.contentLanguageDescription'
                    )}
                  </p>
                  <RadioGroup
                    value={contentLanguage}
                    onValueChange={(nextValue) => {
                      setContentLanguage(nextValue as SupportedLanguage)
                    }}
                    disabled={isCreating}
                    aria-label={t(
                      'detail.complianceStudios.dialog.contentLanguageLabel'
                    )}
                    className='flex flex-wrap items-center gap-3'
                  >
                    {SUPPORTED_LANGUAGES.map((language) => {
                      const itemId = `md-content-language-${language.value}`
                      return (
                        <div
                          key={language.value}
                          className='flex items-center gap-2'
                        >
                          <RadioGroupItem
                            value={language.value}
                            id={itemId}
                          />
                          <Label
                            htmlFor={itemId}
                            className='flex cursor-pointer items-center gap-2 text-sm font-normal'
                          >
                            <span>{language.flag}</span>
                            <span>{language.label}</span>
                          </Label>
                        </div>
                      )
                    })}
                  </RadioGroup>
                </div>
              ) : null}
            </div>
          )}
        </div>

        <DialogFooter className='gap-4 sm:gap-4'>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isCreating}
          >
            {t('dialog.cancel')}
          </Button>
          <Button
            type='button'
            onClick={() => {
              if (effectiveSelectedStudioId) {
                if (effectiveSelectedStudioId === MACHINERY_DIRECTIVE_STUDIO_ID) {
                  onStartStudio(effectiveSelectedStudioId, {
                    contentLanguage: effectiveContentLanguage,
                  })
                  return
                }
                onStartStudio(effectiveSelectedStudioId)
              }
            }}
            disabled={!canStart}
          >
            {isCreating ? (
              <>
                <Loader2 className='me-2 size-4 animate-spin' />
                {t('detail.complianceStudios.dialog.creating')}
              </>
            ) : (
              t('detail.complianceStudios.dialog.create')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
